import { z } from "zod";
import { IsoTimestamp, MatchId, NightId, PlayerId } from "./ids.js";

// ─────────────────────────────────────────────────────────────────────────────
// LOOP 5 — THE VOICE (TOUCHLINE). Part A: the Night Room (one room per night,
// host broadcast presence, slow mode, minors-safe moderation). Part B: THE
// NOTIFICATIONS CENTRE (inbox + prefs + protected tier). One notification
// service: every event → inbox row ALWAYS + channel fan-out per prefs; the
// registry (docs/NOTIFICATIONS.md) is the only place a trigger may be born.
// ─────────────────────────────────────────────────────────────────────────────

// ── Part B: notifications ────────────────────────────────────────────────────
export const NotificationCategory = z.enum(["MATCH", "VAR", "DROPS", "NIGHT", "SYSTEM"]);
export type NotificationCategory = z.infer<typeof NotificationCategory>;

export const NotificationChannel = z.enum(["push", "sms"]);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

/** Registry keys (docs/NOTIFICATIONS.md) — no notification ships outside it. */
export const NotificationKey = z.enum([
  // MATCH (volt)
  "match.challenger_joined", // PULLED UP takeover — protected
  "match.link_live", // lobby armed, link ready — protected
  "match.lobby_closing", // window closing clock — protected
  "match.result_submitted", // opponent submitted — verify window opens
  "match.verify_reminder", // §4.2 verify clock nudge — protected
  "match.ready_check", // bracket round open (Loop 4B) — protected
  // VAR (steel)
  "var.evidence_request", // Tier-3 asks for your screenshot — protected
  "var.decision", // the stamp landed
  "var.confidence_changed", // §6 line on your record
  // DROPS (gold)
  "drops.moment_earned", // a crate waits
  "drops.vault_expiring", // THE VAULT expiry countdown
  "drops.featured_announce", // tonight's featured drop
  // NIGHT (broadcast)
  "night.open", // code live
  "night.room_broadcast", // host said something (opt-in mirror)
  "night.leaderboard_milestone",
  "night.recap_ready",
  "tournament.published", // bracket entries open
  "tournament.round_open", // your round is live — protected while entered
  "tournament.payout_queued",
  // SYSTEM (grey)
  "system.account",
  "system.store",
]);
export type NotificationKey = z.infer<typeof NotificationKey>;

/** The protected tier (B3): undroppable while the player has an active match /
 *  live bracket entry. Grey the toggle with "Active match — always on". */
export const PROTECTED_KEYS: readonly NotificationKey[] = [
  "match.challenger_joined",
  "match.link_live",
  "match.lobby_closing",
  "match.verify_reminder",
  "match.ready_check",
  "var.evidence_request",
  "tournament.round_open",
];

export const KEY_CATEGORY: Record<NotificationKey, NotificationCategory> = {
  "match.challenger_joined": "MATCH",
  "match.link_live": "MATCH",
  "match.lobby_closing": "MATCH",
  "match.result_submitted": "MATCH",
  "match.verify_reminder": "MATCH",
  "match.ready_check": "MATCH",
  "var.evidence_request": "VAR",
  "var.decision": "VAR",
  "var.confidence_changed": "VAR",
  "drops.moment_earned": "DROPS",
  "drops.vault_expiring": "DROPS",
  "drops.featured_announce": "DROPS",
  "night.open": "NIGHT",
  "night.room_broadcast": "NIGHT",
  "night.leaderboard_milestone": "NIGHT",
  "night.recap_ready": "NIGHT",
  "tournament.published": "NIGHT",
  "tournament.round_open": "MATCH",
  "tournament.payout_queued": "DROPS",
  "system.account": "SYSTEM",
  "system.store": "SYSTEM",
};

export const NotificationCard = z.object({
  id: z.string().uuid(),
  key: NotificationKey,
  category: NotificationCategory,
  title: z.string().min(1),
  body: z.string(),
  deeplink: z.string(), // in-app route (magic-link grammar §3.2)
  /** Inline CTA label when actionable ("VERIFY RESULT"); null = informational. */
  cta: z.string().nullable(),
  /** Actionable cards expire gracefully — a lapsed takeover shows its outcome. */
  expires_at: IsoTimestamp.nullable(),
  created_at: IsoTimestamp,
  read_at: IsoTimestamp.nullable(),
  acted_at: IsoTimestamp.nullable(),
});
export type NotificationCard = z.infer<typeof NotificationCard>;

/** Bell badge counts ONLY actionable unread (cta non-null, unexpired, unacted). */
export const InboxView = z.object({
  cards: z.array(NotificationCard),
  actionable_unread: z.number().int().min(0),
});
export type InboxView = z.infer<typeof InboxView>;

export const PrefRow = z.object({
  category: NotificationCategory,
  push: z.boolean(),
  sms: z.boolean(),
  /** True when the row currently contains protected keys locked ON. */
  protected_active: z.boolean(),
});
export const PrefsView = z.object({
  rows: z.array(PrefRow),
  quiet_hours: z.boolean(),
});
export type PrefsView = z.infer<typeof PrefsView>;

export const UpdatePrefsRequest = z.object({
  category: NotificationCategory,
  channel: NotificationChannel,
  enabled: z.boolean(),
});
export type UpdatePrefsRequest = z.infer<typeof UpdatePrefsRequest>;

/** Web-push subscription (PushSubscription.toJSON() shape). */
export const PushSubscribeRequest = z.object({
  endpoint: z.string().url(),
  keys: z.object({ p256dh: z.string().min(1), auth: z.string().min(1) }),
});
export type PushSubscribeRequest = z.infer<typeof PushSubscribeRequest>;

// ── Part A: the Night Room ───────────────────────────────────────────────────
export const RoomMessageKind = z.enum(["player", "broadcast", "system"]);
export type RoomMessageKind = z.infer<typeof RoomMessageKind>;

export const RoomMessage = z.object({
  id: z.string().uuid(),
  night_id: NightId,
  kind: RoomMessageKind,
  /** null for broadcast/system lines. */
  player_id: PlayerId.nullable(),
  gamertag: z.string().nullable(),
  badge_id: z.string().nullable(),
  founder: z.boolean(),
  body: z.string().min(1).max(280),
  created_at: IsoTimestamp,
  /** Hidden for the reporter after a report; ops queue sees everything. */
  hidden: z.boolean(),
});
export type RoomMessage = z.infer<typeof RoomMessage>;

export const RoomView = z.object({
  night_id: NightId,
  open: z.boolean(), // false before open-30min / after freeze
  frozen: z.boolean(),
  slow_mode_seconds: z.number().int().min(0),
  messages: z.array(RoomMessage), // ascending, cursor-paged
  next_cursor: z.string().nullable(),
});
export type RoomView = z.infer<typeof RoomView>;

export const PostRoomMessageRequest = z.object({
  night_code: z.string().min(1),
  body: z.string().min(1).max(280),
});
export type PostRoomMessageRequest = z.infer<typeof PostRoomMessageRequest>;

export const PostRoomMessageResponse = z.object({
  posted: z.boolean(),
  /** Slow-mode rejection: seconds until the player may post again. */
  retry_in_seconds: z.number().int().nullable(),
  message: RoomMessage.nullable(),
});
export type PostRoomMessageResponse = z.infer<typeof PostRoomMessageResponse>;

export const BroadcastRequest = z.object({
  night_code: z.string().min(1),
  body: z.string().min(1).max(280),
  /** Also mirror to the notification rail (night.room_broadcast). */
  notify: z.boolean().default(false),
});
export type BroadcastRequest = z.infer<typeof BroadcastRequest>;

export const ReportMessageRequest = z.object({
  message_id: z.string().uuid(),
  reason_code: z.string().min(1),
});
export type ReportMessageRequest = z.infer<typeof ReportMessageRequest>;

/** DUGOUT report queue row (same console family as VAR Tier 3). */
export const ReportRow = z.object({
  id: z.string().uuid(),
  message: RoomMessage,
  reported_by: PlayerId,
  reason_code: z.string(),
  reported_at: IsoTimestamp,
  report_count: z.number().int().min(1), // distinct reporters on this message
  resolved: z.boolean(),
});
export const ReportQueueView = z.object({ reports: z.array(ReportRow) });
export type ReportRow = z.infer<typeof ReportRow>;
export type ReportQueueView = z.infer<typeof ReportQueueView>;

/** Fan-out decision record — the SMS→push migration instrumentation (B3 ops
 *  note): every notification logs which channels fired and why. */
export const ChannelDecision = z.enum([
  "sent", // channel fired
  "pref_off", // player toggled it off
  "protected_override", // pref off but protected tier forced it
  "no_subscription", // push wanted, no endpoint registered
  "quiet_hours", // batched to morning
  "not_configured", // channel infra off (flag)
  // The flag is ON but this channel has no send path built yet. Exists so a
  // flag flip can never record a send that did not happen (found 25 Jul: the
  // sms branch wrote "sent" without sending — a lying ledger is worse than
  // an off channel). DRIFT-010 replaces this with real sends.
  "not_implemented",
]);
export type ChannelDecision = z.infer<typeof ChannelDecision>;

export { MatchId };
